import { fixZeroDurationTokens } from '@src/editor/services/transcriptValidations';
import { err, ok } from '@src/services/common/Result';
import { extractErrorInfo } from '@src/utility/error';
import {
  getDenseFrameNumbersForSpeaker,
  getFrameNumbersForSpeakerAnalysis,
} from '@src/utility/frameExtraction';
import { LogManager } from '@src/utility/Logger';
import {
  identificationStrength,
  mergeIdentified,
  needsMoreFrames,
} from '@src/utility/speakerIdentification';
import {
  getSpeakerColorFromLabel,
  getSpeakerNameFromLabel,
} from '@src/utility/speakerUtils';

import type { Result } from '@src/services/common/Result';
import type { AnonymousSpeaker } from '@src/services/SpeakerManagementService';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type {
  FrameAnalysisRequest,
  FrameAnalysisResult,
  TranscriptSegment,
} from '@src/utility/frameExtraction';
import type { IdentifiedSpeaker } from '@src/utility/speakerIdentification';

const logger = LogManager.getLogger('service:SpeakerIdentificationService');

/** The speaker row the identification prompts format the transcript with. */
export interface IdentificationSpeaker {
  speakerLabel: string;
  datoSpeakerId: string;
  name: string;
  title: string;
  voice: string;
  bio: string;
  color: string;
  /** Required by the prompt's speaker type; not read on this path. */
  isAnonymous: boolean;
}

/** The LLM calls — `ChatGPTService` satisfies this. */
export interface SpeakerIdentifier {
  identifySpeakers(params: {
    selectedSpeakers?: IdentificationSpeaker[];
  }): Promise<IdentifiedSpeaker[]>;
  identifySpeakersWithFrameAnalysis(params: {
    selectedSpeakers?: IdentificationSpeaker[];
    frameAnalysisResults?: FrameAnalysisResult[];
  }): Promise<IdentifiedSpeaker[]>;
}

/** Reads lower thirds off the story's extracted thumbnails; null on failure. */
export type AnalyzeSpeakerFrames = (
  storyId: string,
  speakerFrames: FrameAnalysisRequest[],
) => Promise<FrameAnalysisResult[] | null>;

export interface SpeakerIdentificationInput {
  storyId: string;
  /** The story's original transcript elements. */
  elements: TranscriptElement[];
  /** The speakers still needing a name. */
  anonymousSpeakers: AnonymousSpeaker[];
}

export interface SpeakerIdentificationState {
  identified: IdentifiedSpeaker[];
  frameAnalysisResults: FrameAnalysisResult[];
  /** Labels the transcript pass left unnamed or only partly named. */
  unidentifiedLabels: string[];
}

export interface IdentificationProgress {
  message?: string;
  analyzingFrames?: boolean;
  frameAnalysisResults?: FrameAnalysisResult[];
}

type OnProgress = (progress: IdentificationProgress) => void;

/**
 * Group consecutive elements by speaker into segments with start/end
 * timestamps. Removed/cut elements and elements without timestamps are skipped
 * (a removed element's `ts` is null at runtime).
 */
export function buildSegmentsFromTranscriptElements(
  elements: TranscriptElement[],
): TranscriptSegment[] {
  const segments: TranscriptSegment[] = [];
  let currentSpeaker = '';
  let currentStart = 0;
  let currentEnd = 0;
  let currentText = '';

  for (const el of elements) {
    if (el.type !== 'text') continue;
    if (el.state === 'removed' || el.state === 'cut') continue;

    const speaker = el.speaker_label || '';
    const ts = el.ts;
    const endTs = el.end_ts;
    if (ts == null || endTs == null) continue;

    if (speaker !== currentSpeaker && currentSpeaker) {
      if (currentText.trim()) {
        segments.push({
          speaker: currentSpeaker,
          start: currentStart,
          end: currentEnd,
          text: currentText.trim(),
        });
      }
      currentSpeaker = speaker;
      currentStart = ts;
      currentEnd = endTs;
      currentText = el.value || '';
    } else {
      if (!currentSpeaker) {
        currentSpeaker = speaker;
        currentStart = ts;
      }
      currentEnd = endTs;
      currentText += el.value || '';
    }
  }

  if (currentSpeaker && currentText.trim()) {
    segments.push({
      speaker: currentSpeaker,
      start: currentStart,
      end: currentEnd,
      text: currentText.trim(),
    });
  }

  return segments;
}

/**
 * The frame endpoint, over fetch. Resolves null on a non-OK response so a
 * failed batch reads as "no evidence" rather than aborting the run.
 */
export const fetchSpeakerFrameAnalysis: AnalyzeSpeakerFrames = async (
  storyId,
  speakerFrames,
) => {
  const arborApiUrl = process.env.REACT_APP_API_URL || window.location.origin;
  const response = await fetch(
    `${arborApiUrl}/api/video/analyze-speaker-frames`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storyId, videoId: storyId, speakerFrames }),
    },
  );
  if (!response.ok) return null;
  const data = await response.json();
  return data.results || [];
};

/**
 * Speaker identification in two stages that need different inputs:
 *
 * - `runTranscriptPass` — the LLM reads the transcript. Needs only the
 *   transcript, so it can run as soon as that exists.
 * - `runFramePasses` — reads on-screen lower thirds for whoever the transcript
 *   left unnamed or only partly named: sparse, then dense, then ultra-dense
 *   frames. Needs the story's extracted thumbnails.
 *
 * Neither stage matches against existing Dato speakers; the modal does that on
 * the result.
 */
export class SpeakerIdentificationService {
  constructor(
    private deps: {
      identifier: SpeakerIdentifier;
      analyzeFrames: AnalyzeSpeakerFrames;
    },
  ) {}

  async runTranscriptPass(
    input: SpeakerIdentificationInput,
    onProgress: OnProgress = () => {},
  ): Promise<Result<SpeakerIdentificationState>> {
    const { anonymousSpeakers } = input;
    try {
      onProgress({
        message: `Analyzing transcript for ${anonymousSpeakers.length} speakers...`,
      });
      const identified = await this.deps.identifier.identifySpeakers({
        selectedSpeakers: buildIdentificationSpeakers(input.elements),
      });

      // A label the transcript named only partially — "Scott", with no
      // surname — is not done. The lower third often carries the full name, so
      // those speakers join the ones pass 1 said nothing about at all.
      const unidentifiedLabels = anonymousSpeakers
        .filter(speaker => {
          const row = identified.find(
            id => id.speakerLabel === speaker.speakerLabel,
          );
          return !row || identificationStrength(row) === 'partial';
        })
        .map(speaker => speaker.speakerLabel);

      logger.debug(
        `Pass 1 complete: ${identified.length} identified, ${unidentifiedLabels.length} unnamed or partial`,
      );
      onProgress({
        message: `${identified.length} of ${anonymousSpeakers.length} speakers identified`,
      });
      return ok({
        identified: [...identified],
        frameAnalysisResults: [],
        unidentifiedLabels,
      });
    } catch (error) {
      logger.error('Transcript pass failed', extractErrorInfo(error));
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Unknown error',
      );
    }
  }

  async runFramePasses(
    input: SpeakerIdentificationInput,
    fromTranscript: SpeakerIdentificationState,
    onProgress: OnProgress = () => {},
  ): Promise<Result<SpeakerIdentificationState>> {
    const { storyId, anonymousSpeakers } = input;
    const allSpeakers = buildIdentificationSpeakers(input.elements);
    const segments = buildSegmentsFromTranscriptElements(input.elements);
    const finalIdentified = [...fromTranscript.identified];
    let frameResults = [...fromTranscript.frameAnalysisResults];

    const recordFrames = (results: FrameAnalysisResult[]) => {
      frameResults = [...frameResults, ...results];
      onProgress({ frameAnalysisResults: frameResults });
    };
    const identifyFromFrames = () =>
      this.deps.identifier.identifySpeakersWithFrameAnalysis({
        selectedSpeakers: allSpeakers,
        frameAnalysisResults: frameResults,
      });
    const progressCount = () =>
      onProgress({
        message: `${finalIdentified.length} of ${anonymousSpeakers.length} speakers identified`,
      });

    try {
      const unidentifiedSpeakers = anonymousSpeakers.filter(speaker =>
        fromTranscript.unidentifiedLabels.includes(speaker.speakerLabel),
      );

      if (unidentifiedSpeakers.length > 0) {
        // PASS 2: Sparse frame analysis
        const partialCount = unidentifiedSpeakers.filter(speaker =>
          finalIdentified.some(id => id.speakerLabel === speaker.speakerLabel),
        ).length;
        onProgress({
          analyzingFrames: true,
          message:
            partialCount === unidentifiedSpeakers.length
              ? `Analyzing video frames to complete ${partialCount} partly named speaker${partialCount === 1 ? '' : 's'}...`
              : `Analyzing video frames for ${unidentifiedSpeakers.length} remaining speakers...`,
        });

        const speakerFrames = getFrameNumbersForSpeakerAnalysis(
          segments.filter(seg =>
            unidentifiedSpeakers.some(
              speaker => speaker.speakerLabel === seg.speaker,
            ),
          ),
        );
        if (speakerFrames.length > 0) {
          try {
            const results = await this.deps.analyzeFrames(
              storyId,
              speakerFrames,
            );
            if (results) {
              recordFrames(results);
              if (results.some(r => r.hasLowerThird)) {
                mergeIdentified(finalIdentified, await identifyFromFrames());
                progressCount();
              }
            }
          } catch (frameError) {
            logger.error(
              'Frame analysis failed:',
              extractErrorInfo(frameError),
            );
          }
        }

        // PASS 3: Dense frame analysis. Climb only where more looking could
        // still pay: a speaker we never read a lower third for. If one was
        // read and it did not name them, sampling the same turn more finely
        // will not either.
        const stillUnidentified = unidentifiedSpeakers.filter(speaker =>
          needsMoreFrames(finalIdentified, frameResults, speaker.speakerLabel),
        );
        if (stillUnidentified.length > 0) {
          onProgress({
            message: `Analyzing more frames for ${stillUnidentified.length} remaining speakers...`,
          });
          const denseFrames = stillUnidentified.flatMap(speaker =>
            getDenseFrameNumbersForSpeaker(
              segments,
              speaker.speakerLabel,
              5,
              10,
            ),
          );
          if (denseFrames.length > 0) {
            try {
              const results = await this.deps.analyzeFrames(
                storyId,
                denseFrames,
              );
              if (results) {
                recordFrames(results);
                if (results.some(r => r.hasLowerThird)) {
                  mergeIdentified(finalIdentified, await identifyFromFrames());
                  progressCount();
                }
              }
            } catch (denseFrameError) {
              logger.error(
                'Dense frame analysis failed:',
                extractErrorInfo(denseFrameError),
              );
            }
          } else {
            progressCount();
          }
        }

        onProgress({ analyzingFrames: false });
      }

      // PASS 4: Ultra-dense frame analysis, same evidence test as pass 3.
      const finalUnidentified = anonymousSpeakers.filter(speaker =>
        needsMoreFrames(finalIdentified, frameResults, speaker.speakerLabel),
      );
      if (finalUnidentified.length > 0) {
        onProgress({
          analyzingFrames: true,
          message: `Deep frame analysis for ${finalUnidentified.length} remaining speakers...`,
        });

        for (const speaker of finalUnidentified) {
          const ultraDenseFrames = getDenseFrameNumbersForSpeaker(
            segments,
            speaker.speakerLabel,
            2,
            30,
          );
          const batchSize = 5;
          let speakerIdentified = false;

          for (
            let i = 0;
            i < ultraDenseFrames.length && !speakerIdentified;
            i += batchSize
          ) {
            const batch = ultraDenseFrames.slice(i, i + batchSize);
            onProgress({
              message: `Analyzing frames ${i + 1}-${Math.min(
                i + batchSize,
                ultraDenseFrames.length,
              )} of ${ultraDenseFrames.length} for ${getSpeakerNameFromLabel(speaker.speakerLabel)}...`,
            });

            try {
              const results = await this.deps.analyzeFrames(storyId, batch);
              if (results?.some(r => r.hasLowerThird)) {
                recordFrames(results);
                const newlyIdentified = (await identifyFromFrames()).find(
                  id => id.speakerLabel === speaker.speakerLabel,
                );
                if (
                  newlyIdentified &&
                  mergeIdentified(finalIdentified, [newlyIdentified]) > 0
                ) {
                  speakerIdentified = true;
                  onProgress({
                    message: `Identified ${getSpeakerNameFromLabel(speaker.speakerLabel)} as ${newlyIdentified.name} (${finalIdentified.length} of ${anonymousSpeakers.length})`,
                  });
                }
              }
            } catch (batchError) {
              logger.error(
                `Pass 4 batch analysis failed for ${speaker.speakerLabel}:`,
                extractErrorInfo(batchError),
              );
            }
          }
        }

        onProgress({ analyzingFrames: false });
      }

      return ok({
        identified: finalIdentified,
        frameAnalysisResults: frameResults,
        unidentifiedLabels: anonymousSpeakers
          .filter(
            speaker =>
              !finalIdentified.some(
                id => id.speakerLabel === speaker.speakerLabel,
              ),
          )
          .map(speaker => speaker.speakerLabel),
      });
    } catch (error) {
      logger.error('Frame passes failed', extractErrorInfo(error));
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Unknown error',
      );
    }
  }
}

/**
 * Every labelled speaker in the transcript, for the prompt's formatting.
 * `ChatGPTService.getTranscript()` formats with these as "Speaker 1: ...",
 * "Speaker 3: ..."; the AI returns 1-based labels which the parser converts
 * back to spk_0, spk_2. Passing none would fall back to raw "spk_0" labels and
 * the parser's -1 offset would produce spk_-1.
 */
function buildIdentificationSpeakers(
  elements: TranscriptElement[],
): IdentificationSpeaker[] {
  const labels = [
    ...new Set(
      elements
        .filter(el => el.type === 'text' && el.speaker_label)
        .map(el => el.speaker_label as string),
    ),
  ];
  return labels.map(label => ({
    speakerLabel: label,
    datoSpeakerId: '',
    name: getSpeakerNameFromLabel(label),
    title: '',
    voice: '',
    bio: '',
    color: getSpeakerColorFromLabel(label),
    isAnonymous: true,
  }));
}

/**
 * A story's original transcript elements, fetched by id — for a story that is
 * not open in the editor. Same lookup as TranscriptionStore.loadTranscription:
 * the self-hosted populate endpoint for AWS stories, Dato otherwise.
 */
export async function fetchStoryTranscriptElements(
  deps: {
    getTranscriptionFromDato: (
      storyId: string,
    ) => Promise<{ elementsJson?: { url?: string } } | null | undefined>;
    requestTranscription: (
      storyId: string,
    ) => Promise<{ elementsJson?: { url?: string } } | null | undefined>;
    fetchTranscriptFromUrl: (
      url: string,
    ) => Promise<{ elements?: TranscriptElement[] }>;
  },
  story: { id: string; useAws?: boolean | null },
): Promise<Result<TranscriptElement[]>> {
  try {
    const transcription = story.useAws
      ? await deps.requestTranscription(story.id)
      : await deps.getTranscriptionFromDato(story.id);
    const url = transcription?.elementsJson?.url;
    if (!url) return err('NOT_FOUND', 'No transcript for story yet');
    const data = await deps.fetchTranscriptFromUrl(url);
    // The same repair TranscriptionStore applies on load, so frame timestamps
    // match what the editor would compute.
    return ok(
      fixZeroDurationTokens(data.elements || []) as TranscriptElement[],
    );
  } catch (error) {
    return err(
      'EXTERNAL',
      error instanceof Error ? error.message : 'Failed to load transcript',
    );
  }
}
